import Decimal from 'decimal.js';

/**
 * Règle de développement n° 1 : aucun montant en virgule flottante.
 *
 * Tout montant, quantité, prix ou pourcentage est un `Decimal`. Les arrondis
 * sont TOUJOURS explicites : aucune fonction de ce module n'arrondit sans
 * qu'on le lui demande, et aucun appelant ne doit arrondir « à la main ».
 */

Decimal.set({
  precision: 34,
  rounding: Decimal.ROUND_HALF_UP,
  toExpNeg: -30,
  toExpPos: 30,
});

export { Decimal };

export const ZERO = new Decimal(0);
export const CENT = new Decimal(100);

/** Décimales de référence, alignées sur le modèle de données (doc 02, § 1). */
export const DECIMALES = {
  montant: 2,
  quantite: 6,
  prix: 6,
  pourcentage: 4,
  parts: 4,
} as const;

/**
 * Construit un montant. Le type refuse volontairement `number` : un littéral
 * JavaScript ne peut pas représenter exactement 0,1 — accepter un `number`
 * ici rouvrirait la porte que la règle n° 1 ferme.
 */
export function montant(valeur: string | Decimal): Decimal {
  const d = valeur instanceof Decimal ? valeur : new Decimal(valeur);
  if (!d.isFinite()) {
    throw new Error(`Montant non fini : ${String(valeur)}`);
  }
  return d;
}

/** Construit une valeur décimale à partir d'un entier exact (quantités, compteurs). */
export function depuisEntier(valeur: number): Decimal {
  if (!Number.isSafeInteger(valeur)) {
    throw new Error(`Entier attendu, reçu : ${valeur}`);
  }
  return new Decimal(valeur);
}

/** Arrondi commercial d'un montant : 2 décimales, moitié vers le haut. */
export function arrondirMontant(valeur: Decimal): Decimal {
  return valeur.toDecimalPlaces(DECIMALES.montant, Decimal.ROUND_HALF_UP);
}

/** Arrondi d'un pourcentage : 4 décimales, moitié vers le haut. */
export function arrondirPourcentage(valeur: Decimal): Decimal {
  return valeur.toDecimalPlaces(DECIMALES.pourcentage, Decimal.ROUND_HALF_UP);
}

/**
 * Arrondi d'un nombre de parts d'OPCVM. Tronqué vers le bas : on n'attribue
 * jamais plus de parts que le montant investi n'en finance.
 */
export function arrondirParts(valeur: Decimal, decimales: number = DECIMALES.parts): Decimal {
  return valeur.toDecimalPlaces(decimales, Decimal.ROUND_DOWN);
}

/**
 * Tronque une quantité de titres au multiple inférieur de la quotité.
 * Un ordre ne porte jamais sur une fraction de titre (RG-08).
 */
export function tronquerQuantite(valeur: Decimal, quotite = 1): Decimal {
  if (quotite <= 0 || !Number.isInteger(quotite)) {
    throw new Error(`Quotité invalide : ${quotite}`);
  }
  const q = new Decimal(quotite);
  return valeur.dividedBy(q).floor().times(q);
}

/** Applique un pourcentage à une assiette, sans arrondir. */
export function pourcentageDe(assiette: Decimal, pourcentage: Decimal): Decimal {
  return assiette.times(pourcentage).dividedBy(CENT);
}

/**
 * Montant d'une ligne à un prix donné, sans arrondir. Un BDT se cote en
 * pourcentage du nominal — cours comme PRU —, une action ou un OPCVM en prix
 * unitaire. Valeur et coût de revient passent par la même règle : les
 * calculer chacun à sa façon fausse la plus-value d'un facteur nominal / 100.
 */
export function montantLigne(
  quantite: Decimal,
  prix: Decimal,
  nominal: Decimal | null | undefined,
): Decimal {
  return nominal ? pourcentageDe(quantite.times(nominal), prix) : quantite.times(prix);
}

/** Poids d'une valeur dans un total, en pourcentage. Total nul → zéro. */
export function poidsPourcent(valeur: Decimal, total: Decimal): Decimal {
  if (total.isZero()) return ZERO;
  return valeur.dividedBy(total).times(CENT);
}

/** Somme exacte d'une liste de décimaux. */
export function somme(valeurs: readonly Decimal[]): Decimal {
  return valeurs.reduce<Decimal>((acc, v) => acc.plus(v), ZERO);
}

/** Sens d'une variation, indépendamment de toute couleur d'affichage. */
export type SensVariation = 'hausse' | 'baisse' | 'nul';

export function sensVariation(valeur: Decimal): SensVariation {
  if (valeur.isZero()) return 'nul';
  return valeur.isPositive() ? 'hausse' : 'baisse';
}

/**
 * Égalité au centime près. Sert aux invariants de bouclage du moteur
 * d'allocation (doc 03, § 3.3, test 13).
 */
export function egalAuCentime(a: Decimal, b: Decimal): boolean {
  return a.minus(b).abs().lessThanOrEqualTo('0.01');
}
